'use client';

import {
  FOCUS_RING,
  INTERACTIVE_SURFACE,
} from '@common/components/interaction';
import SvgCheck from '@common/icon/SvgCheck';
import SvgChevrondown from '@common/icon/SvgChevrondown';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import { useState } from 'react';

import { TERM_CATEGORY } from '@/api/types/parent-code';
import { useGlobalStore } from '@/stores/GlobalStore';

const THIRD_PARTY_TERMS = [
  {
    key: 'membershipTerms',
    label: '고향사랑e음 회원가입을 위한 개인정보 제공 동의',
    category: TERM_CATEGORY.GOHYANG_EUM_SIGNUP_THIRD_PARTY_AGREEMENT,
  },
  {
    key: 'addressVerification',
    label: '주소지 확인을 위한 주민등록번호 제공 동의',
    category: TERM_CATEGORY.RESIDENT_REGISTRATION_PROVIDE_AGREEMENT,
  },
  {
    key: 'shippingInfo',
    label: '답례품 배송 관련 개인정보 제3자 제공 동의',
    category: TERM_CATEGORY.GIFT_DELIVERY_THIRD_PARTY_AGREEMENT,
  },
  {
    key: 'donorManagement',
    label: '개인정보 수집/이용/제공 동의(기부자 관리)',
    category: TERM_CATEGORY.PERSONAL_INFO_COLLECTION_AGREEMENT,
  },
] as const;

const TERM_ITEMS = [
  {
    key: 'welloTerms',
    label: '웰로 고향사랑기부제 이용약관',
    category: TERM_CATEGORY.GOHYANG_LOVE_DONATION_TERMS,
  },
  {
    key: 'serviceTerms',
    label: '웰로 이용약관',
    category: TERM_CATEGORY.SERVICE_TERMS,
  },
  {
    key: 'thirdPartyInfo',
    label: '고향사랑e음 회원가입 및 연계를 위한 개인정보 제3자 제공 동의',
    children: THIRD_PARTY_TERMS,
  },
  {
    key: 'voluntaryDonation',
    label: '공무 관련 강요 없이 자발적으로 기부합니다.',
    category: TERM_CATEGORY.TERMS_GOVERNMENT_PRESSURE,
    isDonationTerm: true,
  },
  {
    key: 'guidanceNotice',
    label: '안내사항(환불 불가·세액공제 등)을 확인했습니다.',
    category: TERM_CATEGORY.TERMS_GUIDANCE_NOTICE,
    isDonationTerm: true,
  },
  {
    key: 'giftInfo',
    label: '답례품 정보 확인 및 동의',
    category: TERM_CATEGORY.TERMS_GIFT_INFO_CHECK_AGREEMENT,
    isDonationTerm: true,
  },
  {
    key: 'marketing',
    label: '마케팅 정보 수신 동의',
    category: TERM_CATEGORY.MARKETING_TERMS,
    isOptional: true,
  },
] as const;

const THIRD_PARTY_KEYS = THIRD_PARTY_TERMS.map(({ key }) => key);

//* thirdPartyInfo는 하위 4건의 파생이라 자기 동의 상태를 갖지 않는다
const AGREEMENT_KEYS = [
  ...TERM_ITEMS.flatMap((item) => ('children' in item ? [] : [item.key])),
  ...THIRD_PARTY_KEYS,
];

const OPTIONAL_KEYS: readonly string[] = TERM_ITEMS.flatMap((item) =>
  'isOptional' in item ? [item.key] : [],
);

const DONATION_KEYS: readonly string[] = TERM_ITEMS.flatMap((item) =>
  'isDonationTerm' in item ? [item.key] : [],
);

type TermKey = (typeof AGREEMENT_KEYS)[number];

const EMPTY_AGREEMENTS = Object.fromEntries(
  AGREEMENT_KEYS.map((key) => [key, false]),
) as Record<TermKey, boolean>;

interface DonationTermsOptions {
  hasMarketingTerm?: boolean;
  hasDonationTerms?: boolean;
}

export const useDonationTerms = ({
  hasMarketingTerm = true,
  hasDonationTerms = true,
}: DonationTermsOptions = {}) => {
  const [agreements, setAgreements] = useState(EMPTY_AGREEMENTS);

  const activeKeys = AGREEMENT_KEYS.filter(
    (key) => hasDonationTerms || !DONATION_KEYS.includes(key),
  );
  const requiredKeys = activeKeys.filter((key) => !OPTIONAL_KEYS.includes(key));
  const agreeableKeys = hasMarketingTerm ? activeKeys : requiredKeys;

  return {
    agreements,
    hasMarketingTerm,
    hasDonationTerms,
    isAllRequiredAgreed: requiredKeys.every((key) => agreements[key]),
    isAllAgreed: agreeableKeys.every((key) => agreements[key]),
    setAgreement: (key: TermKey, checked: boolean) =>
      setAgreements((previous) => ({ ...previous, [key]: checked })),
    setThirdParty: (checked: boolean) =>
      setAgreements((previous) => ({
        ...previous,
        ...Object.fromEntries(THIRD_PARTY_KEYS.map((key) => [key, checked])),
      })),
    agreeAll: () =>
      setAgreements((previous) => ({
        ...previous,
        ...Object.fromEntries(agreeableKeys.map((key) => [key, true])),
      })),
    reset: () => setAgreements(EMPTY_AGREEMENTS),
  };
};

type DonationTermsState = ReturnType<typeof useDonationTerms>;

const TermRow = ({
  label,
  isOptional,
  checked,
  isNested,
  onCheckedChange,
  onOpenDetail,
  expandState,
}: {
  label: string;
  isOptional?: boolean;
  checked: boolean;
  isNested?: boolean;
  onCheckedChange: (checked: boolean) => void;
  onOpenDetail?: () => void;
  expandState?: { isExpanded: boolean; onToggle: () => void };
}) => (
  <li className={cn('flex items-start', isNested && 'pl-16')}>
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      className={cn('shrink-0 rounded-xsmall p-4', FOCUS_RING)}
      onClick={() => onCheckedChange(!checked)}
    >
      <SvgCheck
        className={cn(
          'size-20 transition-colors duration-150 motion-reduce:transition-none',
          checked ? 'text-Gray-black kakaobank:text-O-400' : 'text-Gray-300',
        )}
      />
    </button>
    <button
      type="button"
      aria-label={`${label} 자세히 보기`}
      aria-expanded={expandState ? expandState.isExpanded : undefined}
      className={cn(
        'flex flex-1 items-start gap-4 rounded-xsmall p-4 text-left',
        INTERACTIVE_SURFACE,
      )}
      onClick={() => (expandState ? expandState.onToggle() : onOpenDetail?.())}
    >
      {!isNested && (
        <span className="shrink-0 font-500 text-Gray-black typo-body4">
          {isOptional ? '[선택]' : '[필수]'}
        </span>
      )}
      <span className="flex-1 break-keep font-400 text-Gray-800 typo-body4">
        {label}
      </span>
      {expandState ? (
        <SvgChevrondown
          className={cn(
            'mt-4 size-12 shrink-0 text-Gray-550 transition-transform duration-200 motion-reduce:transition-none',
            expandState.isExpanded && 'rotate-180',
          )}
        />
      ) : (
        <SvgChevronright className="mt-4 size-12 shrink-0 text-Gray-550" />
      )}
    </button>
  </li>
);

export const DonationTermsList = ({
  agreements,
  hasMarketingTerm,
  hasDonationTerms,
  isAllAgreed,
  setAgreement,
  setThirdParty,
  agreeAll,
  reset,
  className,
  isThirdPartyExpandedByDefault = false,
}: DonationTermsState & {
  className?: string;
  isThirdPartyExpandedByDefault?: boolean;
}) => {
  const [isThirdPartyExpanded, setIsThirdPartyExpanded] = useState(
    isThirdPartyExpandedByDefault,
  );
  const setTermsDrawerType = useGlobalStore(
    (state) => state.setTermsDrawerType,
  );

  return (
    <section className={cn('flex flex-col gap-16', className)}>
      <h2 className="font-700 text-Gray-black typo-body2">
        고향사랑기부 서비스 이용에
        <br />
        필요한 동의만 골랐어요
      </h2>

      <button
        type="button"
        role="checkbox"
        aria-checked={isAllAgreed}
        className={cn(
          'flex w-full items-center gap-4 rounded-small p-12',
          'transition duration-150 motion-reduce:transition-none',
          'hover:opacity-80 active:opacity-80',
          FOCUS_RING,
          //* 색이 곧 동의 상태라 hover·active를 배경색으로 덮지 않고 opacity로만 준다
          isAllAgreed
            ? 'bg-Gray-black text-Gray-white kakaobank:bg-O-400'
            : 'bg-Gray-25 text-Gray-black',
        )}
        onClick={() => (isAllAgreed ? reset() : agreeAll())}
      >
        <SvgCheck className="size-24 shrink-0" />
        <span className="font-600 typo-body3">모두 동의</span>
      </button>

      <ul className="flex flex-col gap-4">
        {TERM_ITEMS.filter(
          (item) =>
            (hasMarketingTerm || !('isOptional' in item)) &&
            (hasDonationTerms || !('isDonationTerm' in item)),
        ).map((item) =>
          'children' in item ? (
            <li key={item.key}>
              <ul className="flex flex-col gap-4">
                <TermRow
                  label={item.label}
                  checked={THIRD_PARTY_KEYS.every((key) => agreements[key])}
                  onCheckedChange={setThirdParty}
                  expandState={{
                    isExpanded: isThirdPartyExpanded,
                    onToggle: () => setIsThirdPartyExpanded((open) => !open),
                  }}
                />
                {/* 즉시 언마운트면 바로 아래 버튼이 4행만큼 튄다 — 격자 전이로 흘린다 */}
                <li
                  className={cn(
                    'grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none',
                    isThirdPartyExpanded
                      ? 'grid-rows-[1fr]'
                      : 'grid-rows-[0fr]',
                  )}
                >
                  <ul
                    className="flex flex-col gap-4 overflow-hidden"
                    inert={!isThirdPartyExpanded || undefined}
                  >
                    {item.children.map((child) => (
                      <TermRow
                        key={child.key}
                        isNested
                        label={child.label}
                        checked={agreements[child.key]}
                        onCheckedChange={(checked) =>
                          setAgreement(child.key, checked)
                        }
                        onOpenDetail={() => setTermsDrawerType(child.category)}
                      />
                    ))}
                  </ul>
                </li>
              </ul>
            </li>
          ) : (
            <TermRow
              key={item.key}
              label={item.label}
              isOptional={'isOptional' in item ? item.isOptional : undefined}
              checked={agreements[item.key]}
              onCheckedChange={(checked) => setAgreement(item.key, checked)}
              onOpenDetail={() => setTermsDrawerType(item.category)}
            />
          ),
        )}
      </ul>
    </section>
  );
};
